/* eslint-disable */
import { useCallback, useEffect, useRef, useState } from 'react';
import style from './Carousel.module.scss';

type IDirection = 'horizontal' | 'vertical';
interface ICarousel {
  children?: React.ReactNode;
  className?: string;
  direction?: IDirection;
  prevButton?: React.ComponentType<{ onClick: () => void }>;
  nextButton?: React.ComponentType<{ onClick: () => void }>;
}
class EventHandlerClass {
  functionMap: Record<string, (this: Document, ev: Event) => void> = {};

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  addEventListener(event: string, func: (this: Document, ev: any) => void) {
    this.functionMap[event] = func;
    document.addEventListener(event.split('.')[0], this.functionMap[event]);
  }

  removeEventListener(event: string) {
    document.removeEventListener(event.split('.')[0], this.functionMap[event]);
    delete this.functionMap[event];
  }
}

const EventHandler = new EventHandlerClass();

const animate = ({
  start,
  distance,
  duration,
  callback,
}: {
  start: number;
  distance: number;
  duration: number;
  callback: (progress: number) => void;
}) => {
  const date = Date.now();
  const update = () => {
    const newDate = Date.now();
    const timePassed = newDate - date;
    let progress = timePassed / duration;

    if (progress > 1) progress = 1;

    callback(start + distance * (1 - (1 - progress) ** 2));

    if (progress !== 1) requestAnimationFrame(update);
  };
  update();
};

function Carousel({
  children,
  className,
  direction,
  nextButton: NextButton,
  prevButton: PrevButton,
}: ICarousel): JSX.Element {
  const [uselessPrev, setUselessPrev] = useState(false);
  const [uselessNext, setUselessNext] = useState(false);
  const [hiddenButtons, setHiddenButtons] = useState(false);
  const carouselRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    const parent = carouselRef.current;
    if (!parent) return undefined;
    const child = parent?.children[0] as HTMLElement;
    let isMoving = false;

    function handleClick(eventClick: MouseEvent) {
      if (isMoving) eventClick.preventDefault();
      isMoving = false;
    }

    function handleMouseDown(eventDown: MouseEvent) {
      eventDown.preventDefault();
      let previousScrollPosition = 0;
      let differenceScrollPosition = 0;
      isMoving = false;

      const initialScrollPosition =
        direction === 'vertical'
          ? parent?.scrollTop || 0
          : parent?.scrollLeft || 0;

      function handleMouseMove(eventMove: MouseEvent) {
        eventMove.preventDefault();

        const scrollPosition =
          direction === 'vertical'
            ? parent?.scrollTop || 0
            : parent?.scrollLeft || 0;
        const offsetMouse =
          direction === 'vertical'
            ? eventMove.clientY - eventDown.clientY
            : eventMove.clientX - eventDown.clientX;

        if (Math.abs(offsetMouse) > 5) isMoving = true;

        if (direction === 'vertical' && parent) {
          parent.scrollTop = initialScrollPosition - offsetMouse;
        } else if (parent) {
          parent.scrollLeft = initialScrollPosition - offsetMouse;
        }

        differenceScrollPosition = scrollPosition - previousScrollPosition;
        previousScrollPosition = scrollPosition;
      }

      function handleMouseUp() {
        EventHandler.removeEventListener('mousemove');
        EventHandler.removeEventListener('mouseup');

        const scrollPosition =
          direction === 'vertical'
            ? parent?.scrollTop || 0
            : parent?.scrollLeft || 0;

        animate({
          start: scrollPosition,
          distance: differenceScrollPosition * 20,
          duration: 400,
          callback: (e) => {
            if (direction === 'vertical' && parent) parent.scrollTop = e;
            else if (parent) parent.scrollLeft = e;
          },
        });
      }

      EventHandler.addEventListener('mousemove', handleMouseMove);
      EventHandler.addEventListener('mouseup', handleMouseUp);
    }

    child.addEventListener('click', handleClick, false);
    child.addEventListener('mousedown', handleMouseDown, false);

    return () => {
      child.removeEventListener('click', handleClick, false);
      child.removeEventListener('mousedown', handleMouseDown);
    };
  }, [direction]);

  function prev() {
    const parent = carouselRef.current;
    const child = parent?.children[0];
    const items = child?.children;
    let itemsCumul = 0;

    const initialScrollPosition =
      direction === 'vertical'
        ? parent?.scrollTop || 0
        : parent?.scrollLeft || 0;

    if (items) {
      for (let i = 0; i < items.length; i += 1) {
        const item = items[i] as HTMLElement;
        const dim =
          direction === 'vertical' ? item.offsetHeight : item.offsetWidth;
        itemsCumul += dim;
      }

      for (let i = items.length - 1; i >= 0; i -= 1) {
        const item = items[i] as HTMLElement;
        const dim =
          direction === 'vertical' ? item.offsetHeight : item.offsetWidth;

        if (
          itemsCumul - dim < initialScrollPosition &&
          initialScrollPosition <= itemsCumul
        ) {
          animate({
            start: initialScrollPosition,
            distance: itemsCumul - dim - initialScrollPosition,
            duration: 200,
            callback: (e) => {
              parent?.scrollTo(
                direction === 'vertical' ? { top: e } : { left: e }
              );
            },
          });
          break;
        }

        itemsCumul -= dim;
      }
    }
  }

  function next() {
    const parent = carouselRef.current;
    const child = parent?.children[0];
    const items = child?.children;
    let itemsCumul = 0;

    const initialScrollPosition = Math.ceil(
      direction === 'vertical'
        ? parent?.scrollTop || 0
        : parent?.scrollLeft || 0
    );

    if (items) {
      for (let i = 0; i < items.length; i += 1) {
        const item = items[i] as HTMLElement;
        const dim =
          direction === 'vertical'
            ? item?.offsetHeight || 0
            : item?.offsetWidth || 0;

        if (
          itemsCumul <= initialScrollPosition &&
          initialScrollPosition < itemsCumul + dim
        ) {
          animate({
            start: initialScrollPosition,
            distance: itemsCumul + dim - initialScrollPosition,
            duration: 200,
            callback: (e) => {
              parent?.scrollTo(
                direction === 'vertical' ? { top: e } : { left: e }
              );
            },
          });
          break;
        }

        itemsCumul += dim;
      }
    }
  }

  const checkScrollPosition = useCallback(() => {
    const parent = carouselRef.current;
    const child = parent?.children[0];
    const items = child?.children;

    if (!items || !items.length) return;

    const maxScroll =
      direction === 'vertical'
        ? (child?.clientHeight || 0) - (parent?.offsetHeight || 0)
        : (child?.clientWidth || 0) - (parent?.offsetWidth || 0);
    const scrollPosition =
      direction === 'vertical'
        ? parent?.scrollTop || 0
        : parent?.scrollLeft || 0;

    setUselessPrev(scrollPosition <= 0);
    setUselessNext(scrollPosition >= maxScroll);
  }, [direction]);

  useEffect(() => {
    const parent = carouselRef.current;
    const child = parent?.children[0];
    const items = child?.children;

    checkScrollPosition();

    parent?.addEventListener('scroll', checkScrollPosition);
    window.addEventListener('resize', checkScrollPosition);

    if (items) setHiddenButtons(items.length < 4);

    return () => {
      parent?.removeEventListener('scroll', checkScrollPosition);
      window.removeEventListener('resize', checkScrollPosition);
    };
  }, [checkScrollPosition]);

  return (
    <div className={`${className} ${style.Carousel}`}>
      <div
        className={style.Carousel__content}
        data-direction={direction}
        ref={carouselRef}
      >
        {children}
      </div>
      <div className={style.Carousel__button}>
        {PrevButton && (
          <PrevButton
            onClick={() => prev()}
            data-hidden={hiddenButtons}
            data-useless={uselessPrev}
          />
        )}
        {NextButton && (
          <NextButton
            onClick={() => next()}
            data-hidden={hiddenButtons}
            data-useless={uselessNext}
          />
        )}
      </div>
    </div>
  );
}

Carousel.defaultProps = {
  children: null,
  className: null,
  direction: 'vertical',
  prevButton: null,
  nextButton: null,
};

export type { IDirection };
export default Carousel;
